import { RWSService } from "@rws-framework/client";
import StorageService, { type StorageServiceInstance } from "src/services/StorageService";
import CourseDashboardService, { type CourseDashboardServiceInstance} from "./CourseDashboardService";
import { HTTPClient } from "./httpClient";
import { Cache } from "../helpers/cache";
import Cookies from "js-cookie";
import type { StatsAverage } from "src/types/statsAverage";

export class InsightsDashboardService extends RWSService {
  static _IN_CLIENT: boolean = true;
  private client = new HTTPClient({});
  private cache = new Cache();
  isCourseMock: Record<string, boolean> = {};

  constructor(
    @StorageService private storageService: StorageServiceInstance,
    @CourseDashboardService private dashboardService: CourseDashboardServiceInstance,
  ) {
    super();
  }

  // $stateParams.courseId
  get courseId(): string {
    return window.stateParams.courseId ?? null;
  }

  public flush(): void {
    this.dashboardService.flush('average');
    this.dashboardService.flush('activities');
    this.dashboardService.flush('activity_manager');
    this.client.clearCache(`insights-dashboard-gradebook-${this.courseId}`);
    this.client.clearCacheByPrefix(`insights-dashboard-gradebook-rollup-${this.courseId}`);
    this.cache.clearByPrefix(`insights-dashboard-gradebook-${this.courseId}`);
    this.cache.clearByPrefix(`insights-dashboard-gradebook-rollup-${this.courseId}`);
  }

  public async get(type: string, options = null) {
    const mock = this.isCourseMock[this.courseId];
  
    const resolveMock = async (mockValue: boolean) => {
      // store mock value
      this.isCourseMock[this.courseId] = !!mockValue;
      // adjust type if mock applies
      const finalType = mockValue && type !== "course"
        ? `${type}-mock`
        : type;
  
      return this._get(finalType, options);
    };
  
    // If mock value is already known → use it
    if (mock !== undefined) {
      return resolveMock(mock);
    }
  
    // Otherwise fetch from storageService
    const course: { mock: boolean } = await new Promise((resolve) => {
      this.storageService.get(
        "course",
        { courseId: this.courseId },
        (result) => resolve(result)
      );
    });
  
    return resolveMock(course.mock);
  }

  async getGradebook(): Promise<StatsAverage> {
    // stats:studentgradebook
    return this.client.get(
      `/stats/studentgradebook/${this.courseId}`,
      { cache: `insights-dashboard-gradebook-${this.courseId}` }
    );
  }

  async getGradebookRollup(): Promise<StatsAverage> {
    // stats:studentgradebook
    return this.client.get(
      `/stats/studentgradebook/${this.courseId}`,
      {
        search: { allow_rollup: 'true' },
        cache: `insights-dashboard-gradebook-rollup-${this.courseId}-${this.viewKey}`
      }
    );
  }

  private async _get(type: string, options = null) {
    switch (type) {
      case "course": {
        return this.dashboardService.get('course');
      }
      
      case "gradebook": {
        if (!options?.no_custom_rollup) {
          return this.getGradebookRollup();
        } else {
          return this.getGradebook()
        }
      }

      case "alerts": {
        // TODO Implement dismiss of alert by user and its persistence; now just limit to 2 most recent ones
        return this.dashboardService.get('summary-alerts');
      }

      default: {
        throw new Error("Insights Dashboard GET: valid type is required");
      }      
    }
  }

  private get viewKey(): string {
    return Cookies.get('viewL') || 'custom';
  }


}

export default InsightsDashboardService.getSingleton()

export {InsightsDashboardService as InsightsDashboardServiceInstance}
